import { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

export const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      // Don't show for 7 days after dismiss
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Small delay so it doesn't flash immediately on load
      setTimeout(() => setVisible(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      localStorage.setItem('pwa-install-dismissed', String(Date.now()));
    }
    setDeferredPrompt(null);
    setInstalling(false);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-[380px] z-50 animate-in slide-in-from-bottom-4 duration-500"
      data-testid="pwa-install-banner"
    >
      <div className="relative bg-gradient-to-br from-[#002049] to-[#052f6b] rounded-2xl p-4 shadow-2xl shadow-primary/25 border border-white/10">
        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition-colors"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4 text-white/60" />
        </button>

        <div className="flex items-start gap-3.5">
          {/* Logo */}
          <div className="shrink-0 w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/10">
            <img src="/icon-96x96.png" alt="Horizonte" className="w-9 h-9 rounded-lg" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-white font-semibold text-sm leading-tight">
              Instala Horizonte
            </h3>
            <p className="text-white/60 text-xs mt-0.5 leading-relaxed">
              Acceso rapido desde tu pantalla de inicio, sin necesidad de abrir el navegador
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2.5 mt-3.5">
          <button
            onClick={handleDismiss}
            className="flex-1 py-2.5 text-xs font-medium text-white/50 hover:text-white/80 transition-colors rounded-xl"
            data-testid="pwa-install-later"
          >
            Ahora no
          </button>
          <button
            onClick={handleInstall}
            disabled={installing}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white text-[#002049] font-semibold text-xs rounded-xl hover:bg-white/90 transition-all active:scale-[0.98] disabled:opacity-50"
            data-testid="pwa-install-button"
          >
            {installing ? (
              <div className="w-4 h-4 border-2 border-[#002049] border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Download className="w-3.5 h-3.5" />
                Instalar App
              </>
            )}
          </button>
        </div>

        {/* Subtle device hint */}
        <div className="flex items-center justify-center gap-1 mt-2.5">
          <Smartphone className="w-3 h-3 text-white/20" />
          <span className="text-[10px] text-white/20">Funciona como una app nativa</span>
        </div>
      </div>
    </div>
  );
};
